import React, { useState } from "react";
import "./OnlineBooking.css";
import {
  UpdateSiteContactDetails,
  UpdateWebsiteLink
} from "./Requests";
import CustomButton from "../../Components/Reusable/Button/CustomButton";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { notifyError, notifySuccess } from "../../Response/ResposeHandler";
import { useTranslation } from "react-i18next";

const Details = (props) => {
  const { t } = useTranslation("bookingDetails");

  const [addressNameNumber, setAddressNameNumber] = useState(props.config.addressNumberName);
  const [addressFirstLine, setAddressFirstLine] = useState(props.config.addressFirstLine);
  const [addressSecondLine, setAddressSecondLine] = useState(props.config.addressSecondLine);
  const [addressCity, setAddressCity] = useState(props.config.addressCity);
  const [addressCounty, setAddressCounty] = useState(props.config.addressCounty);
  const [addressPostcode, setAddressPostcode] = useState(props.config.addressPostcode);

  let errorsInfoObj = {
    NameNumber: "",
    firstLine: "",
    secondLine: "",
    city: "",
    county: "",
    mobile: "",
    postcode: ""
  };
  const [infoErrors, setInfoErrors] = useState(errorsInfoObj);

  const [contactButtonLoading, setContactButtonLoading] = useState(false);
  const [websiteButtonLoading, setWebsiteButtonLoading] = useState(false);
  const [website, setWebsite] = useState(
    props.config.websiteLink === null ? null : props.config.websiteLink.replace("https://", "")
  );
  const [phoneNumber, setPhoneNumber] = useState(props.config.phoneNumberForCustomer);
  const [emailAddress, setEmailAddress] = useState(props.config.emailForCustomer);

  function SubmitContactDetails() {
    let error = false;
    const errorInfoObj = { ...errorsInfoObj };

    if (
      !addressNameNumber &&
      !addressFirstLine &&
      !addressSecondLine &&
      !addressCity &&
      !addressCounty &&
      !addressPostcode &&
      !phoneNumber &&
      !emailAddress
    ) {
      // nothing filled, allow empty -> no validation errors (kept original logic)
    } else {
      if (
        !addressNameNumber &&
        !addressFirstLine &&
        !addressSecondLine &&
        !addressCity &&
        !addressCounty &&
        !addressPostcode &&
        (phoneNumber || emailAddress)
      ) {
        // contact methods only is allowed (kept original logic)
      } else {
        if (!addressNameNumber) {
          errorInfoObj.NameNumber = t("details.errors.addressNameNumberRequired");
          error = true;
        }
        if (!addressFirstLine) {
          errorInfoObj.firstLine = t("details.errors.firstLineRequired");
          error = true;
        }
        if (!addressCity) {
          errorInfoObj.city = t("details.errors.cityRequired");
          error = true;
        }
        if (!addressPostcode) {
          errorInfoObj.postcode = t("details.errors.postcodeRequired");
          error = true;
        }
      }
    }

    if (error) {
      notifyError(t("details.toasts.contactValidationFailed"));
    }

    setInfoErrors(errorInfoObj);

    if (!error) {
      setContactButtonLoading(true);
      UpdateSiteContactDetails(
        addressNameNumber,
        addressFirstLine,
        addressSecondLine,
        addressCity,
        addressCounty,
        addressPostcode,
        phoneNumber,
        emailAddress
      )
        .then(() => {
          notifySuccess(t("details.toasts.contactSaved"));
        })
        .catch(() => {
          notifyError(t("details.toasts.genericError"));
        })
        .finally(() => {
          setContactButtonLoading(false);
          props.getConfig();
        });
    }
  }

  function SubmitWebsite() {
    setWebsiteButtonLoading(true);
    UpdateWebsiteLink("https://" + String(website || "").replace("https://", ""))
      .then((res) => {
        if (res.status === 200) {
          notifySuccess(t("details.toasts.websiteSaved"));
        }
      })
      .catch(() => {
        notifyError(t("details.toasts.websiteSaveError"));
      })
      .finally(() => {
        setWebsiteButtonLoading(false);
        props.getConfig();
      });
  }

  return (
    <div className="infoWrap">
      {/* <ToastContainer /> */}

      {/* Website card */}
      <section className="infoCard">
        <header className="infoHead">
          <h3>{t("details.website.title")}</h3>
          <p className="infoSub">{t("details.website.subtitle")}</p>
        </header>

        <div className="infoBody">
          <div className="sm-input-affix">
            <span className="sm-input-prefix" aria-hidden="true">https://</span>
            <input
              name="website"
              type="text"
              style={{ paddingLeft: "4.2rem" }}
              className="sm-input sm-input-with-prefix"
              placeholder={t("details.placeholders.website")}
              value={website ?? ""}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <div className="infoActions">
            <CustomButton
              disabled={websiteButtonLoading}
              className="app-btn--sm"
              onClick={SubmitWebsite}
              label={t("details.actions.save")}
              loading={websiteButtonLoading}
            />
          </div>
        </div>
      </section>

      {/* Contact details card */}
      <section className="infoCard">
        <header className="infoHead">
          <h3>{t("details.contact.title")}</h3>
          <p className="infoSub">{t("details.contact.subtitle")}</p>
        </header>

        <div className="infoBody">
          <div className="infoGrid">
            {/* Number/Name */}
            <div className={`sm-field ${infoErrors.NameNumber ? "is-error" : ""}`}>
              <label className="sm-label" htmlFor="address-number-name">
                {t("details.labels.numberOrName")}
              </label>
              <input
                id="address-number-name"
                type="text"
                maxLength={45}
                className="sm-input"
                placeholder={t("details.placeholders.numberOrName")}
                value={addressNameNumber || ""}
                onChange={(e) => setAddressNameNumber(e.target.value)}
              />
              {infoErrors.NameNumber && <div className="sm-error">{infoErrors.NameNumber}</div>}
            </div>

            {/* First line */}
            <div className={`sm-field ${infoErrors.firstLine ? "is-error" : ""}`}>
              <label className="sm-label" htmlFor="address-first-line">
                {t("details.labels.firstLine")}
              </label>
              <input
                id="address-first-line"
                type="text"
                maxLength={45}
                className="sm-input"
                placeholder={t("details.placeholders.firstLine")}
                value={addressFirstLine || ""}
                onChange={(e) => setAddressFirstLine(e.target.value)}
              />
              {infoErrors.firstLine && <div className="sm-error">{infoErrors.firstLine}</div>}
            </div>

            {/* Second line */}
            <div className={`sm-field ${infoErrors.secondLine ? "is-error" : ""}`}>
              <label className="sm-label" htmlFor="address-second-line">
                {t("details.labels.secondLine")}
              </label>
              <input
                id="address-second-line"
                type="text"
                maxLength={45}
                className="sm-input"
                placeholder={t("details.placeholders.secondLine")}
                value={addressSecondLine || ""}
                onChange={(e) => setAddressSecondLine(e.target.value)}
              />
              {infoErrors.secondLine && <div className="sm-error">{infoErrors.secondLine}</div>}
            </div>

            {/* City */}
            <div className={`sm-field ${infoErrors.city ? "is-error" : ""}`}>
              <label className="sm-label" htmlFor="address-city">
                {t("details.labels.city")}
              </label>
              <input
                id="address-city"
                type="text"
                maxLength={45}
                className="sm-input"
                placeholder={t("details.placeholders.city")}
                value={addressCity || ""}
                onChange={(e) => setAddressCity(e.target.value)}
              />
              {infoErrors.city && <div className="sm-error">{infoErrors.city}</div>}
            </div>

            {/* County */}
            <div className={`sm-field ${infoErrors.county ? "is-error" : ""}`}>
              <label className="sm-label" htmlFor="address-county">
                {t("details.labels.county")}
              </label>
              <input
                id="address-county"
                type="text"
                maxLength={45}
                className="sm-input"
                placeholder={t("details.placeholders.county")}
                value={addressCounty || ""}
                onChange={(e) => setAddressCounty(e.target.value)}
              />
              {infoErrors.county && <div className="sm-error">{infoErrors.county}</div>}
            </div>

            {/* Postcode */}
            <div className={`sm-field ${infoErrors.postcode ? "is-error" : ""}`}>
              <label className="sm-label" htmlFor="address-postcode">
                {t("details.labels.postcode")}
              </label>
              <input
                id="address-postcode"
                type="text"
                className="sm-input"
                placeholder={t("details.placeholders.postcode")}
                value={addressPostcode || ""}
                onChange={(e) => setAddressPostcode(e.target.value)}
              />
              {infoErrors.postcode && <div className="sm-error">{infoErrors.postcode}</div>}
            </div>

            {/* Email */}
            <div className="sm-field">
              <label className="sm-label" htmlFor="email">
                {t("details.labels.emailAddress")}{" "}
                <span className="sm-hint">{t("details.hints.emailHint")}</span>
              </label>
              <input
                id="email"
                type="email"
                maxLength={45}
                className="sm-input"
                placeholder={t("details.placeholders.email")}
                value={emailAddress || ""}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
            </div>

            {/* Phone */}
            <div className={`sm-field ${infoErrors.mobile ? "is-error" : ""}`}>
              <label className="sm-label" htmlFor="tel">
                {t("details.labels.phoneNumber")}{" "}
                <span className="sm-hint">{t("details.hints.phoneHint")}</span>
              </label>
              <input
                id="tel"
                type="tel"
                maxLength={12}
                className="sm-input"
                placeholder={t("details.placeholders.phone")}
                value={phoneNumber || ""}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              {infoErrors.mobile && <div className="sm-error">{infoErrors.mobile}</div>}
            </div>
          </div>

          <div className="infoActions">
            <CustomButton
              disabled={contactButtonLoading}
              className="app-btn--sm"
              onClick={SubmitContactDetails}
              label={t("details.actions.save")}
              loading={contactButtonLoading}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Details;
